
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import type { QoutAllRow } from '@/pages/customCalculation/model';
import { formatDateTimeToYYYYMMDDHHMM } from '@/functions';
import { useSysInfoStore, useCustomCalcResultAllStore } from '@/shared/service';
import { generateAPI } from '@/shared/lib';
import QoutAllTable from './QoutAllTable.vue';
import BulkQoutAllOperation from './BulkQoutAllOperation.vue';

@Component({
  components: {
    QoutAllTable,
    BulkQoutAllOperation,
  },
})
export default class SettingQoutAllConditions extends Vue {
  @Prop({ type: Function, required: true })
  onBackToRainfall!: () => void;

  @Prop({ type: Function, required: true })
  onCalculationExecuted!: () => void;

  @Prop({ type: Function, required: true })
  setManualCalculationFileName!: (fileName: string) => void;

  @Prop({ type: String, required: true })
  targetTime!: string;

  @Prop({ type: Function, required: true })
  setTargetTime!: (value: string) => void;

  @Prop({ type: String, required: false, default: '2200-01-01 00:00' })
  latestTargetTime!: string;

  @Prop({ type: String, required: true })
  calculationFileName!: string;

  private customCalcResultAllStore = useCustomCalcResultAllStore();

  qoutAllData: QoutAllRow[] = [];

  isCalculating = false;

  pollingIntervalId: number | null = null;

  get basinId() {
    return this['$store'].getters.currentContent.resultKey;
  }

  async fetchCustomCalcResultAll() {
    if (!this.calculationFileName) {
      return;
    }
    
    try {
      await this.customCalcResultAllStore.fetch(this.basinId, this.calculationFileName);
      this.generateQoutAllData();
    } catch (error) {
      console.error('customCalc結果取得エラー:', error);
    }
  }

  mounted() {
    this.fetchCustomCalcResultAll();
  }

  generateQoutAllData() {
    const { allResults } = this.customCalcResultAllStore;
    if (!allResults || !allResults.stations || allResults.stations.length === 0) {
      this.qoutAllData = [];
      return;
    }

    const sysInfoStore = useSysInfoStore();
    const { stationsMap } = sysInfoStore;

    // type=7（ダム）の観測所のみをフィルタリング
    const damStations = allResults.stations.filter(station => {
      const stationInfo = stationsMap?.[station.id];
      return stationInfo && stationInfo.type === 7;
    });

    if (damStations.length === 0) {
      this.qoutAllData = [];
      return;
    }

    // すべてのダム観測所からユニークな時間を取得
    const datetimeSet = new Set<string>();
    damStations.forEach(station => {
      station.simResult.forEach(data => {
        datetimeSet.add(data.datetime);
      });
    });

    // 時間でソート
    const sortedDatetimes = Array.from(datetimeSet).sort();

    // 6時間前のデータをスキップ（6時間 × 60分 ÷ 10分 = 36個）
    // 10分刻みなので1時間に6個のデータがある
    // インデックス0が6時間前、インデックス36が現時刻（0時間目）
    const skipCount = 36;
    const filteredDatetimes = sortedDatetimes.slice(skipCount);

    // 1時間単位にフィルタリング（6個に1個を抽出）
    const hourlyDatetimes = filteredDatetimes.filter((_, index) => index % 6 === 0);

    // 時間ごとにQoutAllRowを作成（ダムのみ）
    this.qoutAllData = hourlyDatetimes.map((datetime, index) => {
      const dams = damStations.map(station => {
        const simData = station.simResult.find(d => d.datetime === datetime);
        const damName = stationsMap?.[station.id]?.name ?? station.id;
        return {
          id: station.id,
          label: `${damName}放流量 [m³/s]`,
          before: simData?.qoutAll ?? 0,
          after: simData?.qoutAll ?? 0,
        };
      });

      return {
        label: index === 0 ? '現時刻' : `${index}時間後`,
        dateTime: datetime,
        dams,
      };
    });
  }

  onOperationUpdated() {
    // 更新を検出するため、再レンダリングをトリガー
    this.$forceUpdate();
  }

  /**
   * 放流量データをAPI送信用フォーマットに変換
   */
  formatApiData() {
    const formattedTime = formatDateTimeToYYYYMMDDHHMM(this.targetTime);

    const sysInfoStore = useSysInfoStore();
    const { customResultKey } = sysInfoStore;
    
    const targetFile = `results/${customResultKey}/${this.calculationFileName}.zip`;
    
    const apiData: { [key: string]: any } = {
      targetFile,
    };

    // 各観測所のデータを作成
    if (this.qoutAllData.length > 0) {
      this.qoutAllData[0].dams.forEach(dam => {
        const stationId = dam.id;
        const qoutValues: number[] = [];
        
        // 各時間の放流量を取得（after値を使用）
        this.qoutAllData.forEach(row => {
          const damData = row.dams.find(d => d.id === stationId);
          if (damData) {
            const value = typeof damData.after === 'string' ? parseFloat(damData.after) : damData.after;
            qoutValues.push(value || 0);
          }
        });

        apiData[stationId] = {
          Qout: qoutValues,
        };
      });
    }

    return apiData;
  }

  /**
   * 計算実行ボタンのクリックハンドラ
   */
  async executeCalculation() {
    if (this.isCalculating) {
      return;
    }

    try {
      this.isCalculating = true;
      
      const apiData = this.formatApiData();
      console.log('API送信用放流量データ:', JSON.stringify(apiData, null, 2));
      
      const response = await generateAPI().customCalcApi.saveCustomCalcConditionApiApiCustomCalcConditionPost({ body: apiData });
      console.log('計算リクエスト送信完了:', response);

      // 既存のポーリングがあれば停止
      this.stopPolling();

      // 結果をポーリング
      this.startPolling(response.filename);
      
    } catch (error) {
      console.error('計算実行エラー:', error);
      this.isCalculating = false;
    }
  }

  /**
   * ポーリングを開始
   */
  startPolling(filename: string) {
    this.pollingIntervalId = window.setInterval(async () => {
      try {
        const result = await generateAPI().customCalcApi.checkCustomCalcResultExistsApiApiCustomCalcExistsBasinIdGet({
          basinId: this.basinId,
          filename,
        });
        
        if (result.fileExists) {
          this.stopPolling();
          this.isCalculating = false;
          this.setManualCalculationFileName(filename);
          this.onCalculationExecuted();
        }
      } catch (error) {
        console.error('ポーリングエラー:', error);
        this.stopPolling();
        this.isCalculating = false;
      }
    }, 10000);
  }

  /**
   * ポーリングを停止
   */
  stopPolling() {
    if (this.pollingIntervalId !== null) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  /**
   * コンポーネント破棄時にポーリングをクリーンアップ
   */
  beforeDestroy() {
    this.stopPolling();
  }
}
